import { Box, CircularProgress, Grid, makeStyles, Theme, Typography, Tooltip } from "@material-ui/core";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { theme } from "../../../../theme/theme";
import { HelpOutline, ArrowCircleRight, ArrowCircleLeft } from "@mui/icons-material";
import { ReferenceModal } from "../../../../components/modal/reference-modal";

interface IGeneVariationPopulationCardStyleProps {
  population: number | undefined | null;
}

const useStyles = makeStyles((theme: Theme) => ({
  cardContainer: {
    border: `2px solid #d8d8d8`,
    borderRadius: 16,
    padding: theme.spacing(4),
    [theme.breakpoints.down(380)]: {
      padding: theme.spacing(2),
    },
  },
  graphicContainer: {
    position: "relative",
    width: "45px",
    height: "45px",
    [theme.breakpoints.down(330)]: {
      width: 35,
      height: 35,
    },
  },
  progress: {
    display: ({ population }: IGeneVariationPopulationCardStyleProps) => (population ? "block" : "none"),
    position: "absolute",
    height: "100%",
    color: theme.palette.primary.main,
  },
  progressBg: {
    display: ({ population }: IGeneVariationPopulationCardStyleProps) => (population ? "block" : "none"),
    position: "absolute",
    height: "100%",
    color: "#d8d8d8",
  },
  icon: {
    display: ({ population }: IGeneVariationPopulationCardStyleProps) => (population ? "block" : "none"),
    position: "absolute",
    height: "50%",
    left: "55%",
    top: "50%",
    fontSize: 12,
    fontWeight: "bold",
    transform: "translate(-50%, -50%)",
  },
  arrowRight: {
    height: 24,
    width: 24,
    paddingRight: theme.spacing(2),
    [theme.breakpoints.down(380)]: {
      height: 20,
      width: 20,
      paddingRight: theme.spacing(2),
    },
  },
  label: {
    fontWeight: "bold",
    width: 82,
    [theme.breakpoints.down(380)]: {
      fontSize: 14,
      width: 70,
    },
  },
  value: {
    [theme.breakpoints.down(380)]: {
      fontSize: 14,
      width: 70,
    },
  },
  infoIcon: {
    position: "relative",
    left: 28,
    top: -8,
    cursor: "pointer",
    backgroundColor: theme.palette.common.white,
    [theme.breakpoints.down(380)]: {},
  },
  tooltip: {
    padding: 0,
    color: "transparent",
    backgroundColor: "transparent",
  },
}));

interface IGeneVariationPopulationCardProps {
  name: string;
  refsnp: string;
  genoType: string;
  population: number | undefined | null;
  orientation: string;
  references: string[];
}

export const GeneVariationPopulationCard: React.VFC<IGeneVariationPopulationCardProps> = (props) => {
  const { name, refsnp, genoType, population, orientation, references } = props;
  const classes = useStyles({ population });
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState<boolean>(false);

  return (
    <>
      <ReferenceModal references={references} open={openModal} onClose={() => setOpenModal(false)} />
      <Grid container className={classes.cardContainer} alignItems="center" justify="space-between">
        <Grid item xs={"auto"}>
          <Grid item container direction="row" alignItems="center">
            <Grid item xs="auto">
              <Box className={classes.arrowRight}>
                <ArrowCircleRight
                  style={orientation === "plus" ? { color: theme.palette.primary.main } : { color: "#d8d8d8" }}
                />
              </Box>
            </Grid>
            <Grid item xs="auto">
              <Typography className={classes.label}>{name}</Typography>
            </Grid>
            <Grid item xs="auto" style={{ flexGrow: 1 }}></Grid>
          </Grid>
          <Grid item container direction="row" alignItems="center">
            <Grid item xs="auto">
              <Box className={classes.arrowRight}>
                <ArrowCircleLeft
                  style={orientation === "minus" ? { color: theme.palette.primary.main } : { color: "#d8d8d8" }}
                />
              </Box>
            </Grid>
            <Grid item xs="auto">
              <Typography className={classes.label}>{t(`gene.refsnp`)}</Typography>
            </Grid>
            <Grid item xs="auto" style={{ flexGrow: 1 }}>
              <Typography className={classes.value}>{refsnp}</Typography>
            </Grid>
          </Grid>
          <Grid item container direction="row" alignItems="center">
            <Grid item xs="auto">
              <Box className={classes.arrowRight}></Box>
            </Grid>
            <Grid item xs="auto">
              <Typography className={classes.label}>{t(`gene.genoTypeNoSpace`)}</Typography>
            </Grid>
            <Grid item xs="auto" style={{ flexGrow: 1 }}>
              <Typography className={classes.value}>{genoType}</Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Grid item className={classes.graphicContainer} xs={"auto"}>
            <Typography className={classes.icon}>{population}%</Typography>
            <CircularProgress
              size={"100%"}
              variant="determinate"
              thickness={3}
              value={100}
              className={classes.progressBg}
            />
            <CircularProgress
              size={"100%"}
              variant="determinate"
              thickness={3}
              value={population ?? 0}
              className={classes.progress}
            />
            {references.length > 0 && (
              <Tooltip title={"Help"} classes={{ tooltip: classes.tooltip }} onClick={() => setOpenModal(true)}>
                <HelpOutline className={classes.infoIcon} />
              </Tooltip>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
